import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/useAuthStore';
import { resolvePostLoginRoute } from '../../utils/postLoginRoute';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { refreshSession } = useAuthStore();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [status, setStatus] = useState('Completing sign-in…');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    (async () => {
      // supabase-js v2 auto-handles both ?code= (PKCE) and #access_token=
      // (implicit) URL patterns when getSession() is called.
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !sessionData.session) {
        setErrorMessage(sessionError?.message ?? 'No session returned from OAuth provider.');
        return;
      }

      const userId = sessionData.session.user.id;

      // Backfill source_app on auth.users metadata. Google OAuth cannot carry
      // options.data, so we write it here.
      setStatus('Finalising account setup…');
      const { error: updateError } = await supabase.auth.updateUser({
        data: { source_app: 'lead-portal' },
      });
      if (updateError) {
        console.error('updateUser failed:', updateError.message);
      }

      // Authoritative write to public.users. Idempotent — safe even if the
      // trigger already inserted a row with source_app=null.
      const { error: rpcError } = await supabase.rpc('ensure_lead_user', {
        p_user_id: userId,
        p_source_app: 'lead-portal',
      });
      if (rpcError) {
        console.error('ensure_lead_user RPC failed:', rpcError.message);
      }

      // Force-issue a new JWT so the custom_access_token_hook picks up the
      // now-committed role.
      await refreshSession();

      // Route to whichever step this user left off on. New OAuth users have no
      // signed agreement yet, so they resolve to /sign; returning users get
      // bumped further along the funnel.
      const userRole = useAuthStore.getState().userRole;
      const route = await resolvePostLoginRoute(userId, {
        isEmployee: userRole === 'employee',
      });
      navigate(route, { replace: true });
    })();
  }, [navigate, refreshSession]);

  if (errorMessage) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="max-w-sm text-center">
          <p className="text-sm text-destructive">{errorMessage}</p>
          <a
            href="/login"
            className="mt-4 inline-block text-sm text-primary underline underline-offset-4"
          >
            Back to sign-up
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground">
      {status}
    </div>
  );
}
